import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import emailjs from '@emailjs/browser';
import { 
    ChefHat, 
    Utensils, 
    UtensilsCrossed,
    Soup,
    Salad,
    Beef,
    Cake,
    Coffee,
    Wine,
    CookingPot,
    Flame,
    Phone,
    Mail,
    MapPin,
    Menu,
    X,
    Send,
    ArrowRight,
    ArrowDown,
    ChevronLeft,
    ChevronRight,
    Quote
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// EmailJS Configuration
const EMAILJS_SERVICE_ID = "service_2e12jx1";
const EMAILJS_TEMPLATE_ID = "template_us6b0e8";
const EMAILJS_PUBLIC_KEY = "JtiGfqSoIdQQgK8oF";

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cucharas-landing/artifacts/nqs7qjjc_Logo%20Cucharas%20PNG.png";

// Images
const IMAGES = {
    hero: "https://customer-assets.emergentagent.com/job_cucharas-landing/artifacts/riskiwel_chefs-sonriendo.jpg",
    gala: "https://images.unsplash.com/photo-1768508947591-b650dacc70a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwY2F0ZXJpbmclMjBldmVudCUyMGdhbGElMjBkaW5uZXIlMjBzZXR1cHxlbnwwfHx8fDE3NzUwNjQwMDV8MA&ixlib=rb-4.1.0&q=85",
    catering: "https://images.unsplash.com/photo-1735044029202-c7f40cc37c55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwY2F0ZXJpbmclMjBldmVudCUyMGdhbGElMjBkaW5uZXIlMjBzZXR1cHxlbnwwfHx8fDE3NzUwNjQwMDV8MA&ixlib=rb-4.1.0&q=85",
    industrial: "https://images.pexels.com/photos/18429457/pexels-photo-18429457.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    plating: "https://images.unsplash.com/photo-1759741558362-9dd97b848ae4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxnb3VybWV0JTIwY2hlZiUyMHBsYXRpbmclMjBmb29kJTIwZmluZSUyMGRpbmluZ3xlbnwwfHx8fDE3NzUwNjQwMDV8MA&ixlib=rb-4.1.0&q=85",
    elegantTable: "https://images.pexels.com/photos/14646764/pexels-photo-14646764.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
};

// Gallery Images
const GALLERY_IMAGES = [
    {
        url: "https://customer-assets.emergentagent.com/job_cucharas-landing/artifacts/7yrgtc08_Imagen1.jpg",
        alt: "Equipo de chefs en la cocina",
        caption: "Nuestro equipo profesional"
    },
    {
        url: "https://customer-assets.emergentagent.com/job_cucharas-landing/artifacts/fcni1a9c_Imagen2.jpg",
        alt: "Postres artesanales",
        caption: "Postres de alta repostería"
    },
    {
        url: "https://customer-assets.emergentagent.com/job_cucharas-landing/artifacts/rsnuxxig_Imagen3.jpg",
        alt: "Platillo gourmet",
        caption: "Platillos con presentación impecable"
    },
    {
        url: "https://customer-assets.emergentagent.com/job_cucharas-landing/artifacts/879wrsmi_Imagen4.jpg",
        alt: "Chef preparando ingredientes",
        caption: "Ingredientes frescos y de calidad"
    }
];

// Header Component
const Header = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navItems = [
        { label: "Inicio", href: "#inicio" },
        { label: "Servicios", href: "#servicios" },
        { label: "Nosotros", href: "#nosotros" },
        { label: "Testimonios", href: "#testimonios" }
    ];

    return (
        <header 
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                isScrolled ? 'bg-white shadow-sm py-3' : 'bg-transparent py-5'
            }`}
            data-testid="header"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="flex items-center justify-between">
                    {/* Logo */}
                    <a href="#inicio" className="flex items-center" data-testid="logo-link">
                        <img 
                            src={LOGO_URL} 
                            alt="Cucharas y Sabores" 
                            className={`transition-all duration-300 ${isScrolled ? 'h-12' : 'h-14 brightness-0 invert'}`}
                        />
                    </a>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center gap-10">
                        {navItems.map((item) => (
                            <a
                                key={item.label}
                                href={item.href}
                                className={`text-sm tracking-wide font-medium transition-colors hover:text-[#BF6CE4] ${
                                    isScrolled ? 'text-gray-700' : 'text-white'
                                }`}
                                data-testid={`nav-${item.label.toLowerCase()}`}
                            >
                                {item.label}
                            </a>
                        ))}
                    </nav>

                    {/* CTA Button */}
                    <a
                        href="#contacto"
                        className="hidden lg:flex items-center gap-2 px-6 py-3 rounded-full border-2 border-[#BF6CE4] text-[#BF6CE4] hover:bg-[#BF6CE4] hover:text-white transition-all duration-300 text-sm font-semibold"
                        data-testid="header-cta"
                    >
                        Contacto
                        <ArrowRight size={16} />
                    </a>

                    {/* Mobile Menu Button */}
                    <button
                        className={`lg:hidden p-2 ${isScrolled ? 'text-gray-700' : 'text-white'}`}
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        data-testid="mobile-menu-toggle"
                    >
                        {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
                <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg" data-testid="mobile-menu">
                    <nav className="flex flex-col p-6 gap-4">
                        {navItems.map((item) => (
                            <a
                                key={item.label}
                                href={item.href}
                                className="text-gray-700 hover:text-[#BF6CE4] transition-colors font-medium py-2 text-lg"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                {item.label}
                            </a>
                        ))}
                        <a
                            href="#contacto"
                            className="mt-4 flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#BF6CE4] text-white font-semibold"
                            onClick={() => setIsMobileMenuOpen(false)}
                        >
                            Contacto
                            <ArrowRight size={16} />
                        </a>
                    </nav>
                </div>
            )}
        </header>
    );
};

// Hero Section - Full Screen
const HeroSection = () => {
    return (
        <section id="inicio" className="relative h-screen flex items-center" data-testid="hero-section">
            {/* Background Image - Full Cover */}
            <div className="absolute inset-0 z-0">
                <img 
                    src={IMAGES.hero} 
                    alt="Soluciones gastronómicas"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
                <div className="max-w-3xl">
                    <h1 className="text-4xl sm:text-5xl lg:text-7xl font-serif font-medium text-white leading-tight">
                        Soluciones gastronómicas<br />
                        <span className="text-[#FFD966]">para empresas</span>
                    </h1>
                    <p className="mt-8 text-lg sm:text-xl text-white/90 leading-relaxed max-w-xl">
                        Somos un grupo gastronómico que ofrece servicios de alimentación con base en las necesidades de cada uno de nuestros clientes.
                    </p>
                </div>
            </div>

            {/* Scroll Indicator */}
            <div className="absolute bottom-10 right-10 z-10">
                <a 
                    href="#servicios" 
                    className="w-14 h-14 rounded-full border-2 border-white/50 flex items-center justify-center text-white/70 hover:border-white hover:text-white transition-all duration-300"
                >
                    <ArrowDown size={24} />
                </a>
            </div>
        </section>
    );
};

// Intro Section
const IntroSection = () => {
    return (
        <section className="py-24 lg:py-32 bg-white" data-testid="intro-section">
            <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-gray-900 leading-tight">
                    Alimentación de alta calidad,
                    <br />
                    <span className="text-[#BF6CE4]">saludable y deliciosa</span>
                </h2>
                <p className="mt-8 text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
                    Gracias a nuestro modelo de operación basado en la calidad de nuestros insumos, 
                    higiene, servicio personalizado, flexibilidad y mejora continua, nos hemos convertido 
                    en el aliado gastronómico de las mejores empresas de la región.
                </p>
            </div>
        </section>
    );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
    const reasons = [
        {
            icon: <UtensilsCrossed strokeWidth={1.5} size={32} />,
            title: "Pasión Culinaria",
            description: "Ponemos el corazón en cada receta, respetando los ingredientes y las técnicas tradicionales y vanguardistas."
        },
        {
            icon: <ChefHat strokeWidth={1.5} size={32} />,
            title: "Higiene Rigurosa",
            description: "Mantenemos estándares de limpieza impecables en todos nuestros procesos de preparación y servicio."
        },
        {
            icon: <Salad strokeWidth={1.5} size={32} />,
            title: "Hospitalidad",
            description: "Nos enfocamos en el servicio al cliente, asegurando que cada comensal se sienta bienvenido y atendido."
        },
        {
            icon: <Flame strokeWidth={1.5} size={32} />,
            title: "Versatilidad",
            description: "Capacidad de adaptarnos a cualquier formato, desde comedores institucionales hasta grandes banquetes y ferias."
        }
    ];

    return (
        <section className="py-24 lg:py-32 bg-[#FFD966]" data-testid="why-choose-us-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    <span className="text-[#BF6CE4] font-semibold text-sm tracking-widest uppercase">
                        Nuestros Diferenciadores
                    </span>
                    <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-gray-900">
                        ¿Por qué elegirnos?
                    </h2>
                    <p className="mt-8 text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto">
                        Lo que nos distingue como organización es nuestra capacidad y conocimiento integral en el sector alimenticio, donde no solo cocinamos, sino que vestimos y ambientamos tu evento por completo. A diferencia de nuestra competencia, en Cucharas y Sabores fusionamos la eficiencia de un comedor industrial con el detalle de un restaurante de autor. Nuestra flexibilidad nos permite pasar de un servicio de autoservicio masivo a una cena de gala con meseros sin perder la esencia del sazón casero que nos caracteriza.
                    </p>
                    <p className="mt-6 text-lg text-gray-800 font-medium max-w-4xl mx-auto">
                        En Cucharas y Sabores nuestro compromiso es atender cada servicio bajo los siguientes pilares:
                    </p>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {reasons.map((reason, index) => (
                        <div 
                            key={index}
                            className="bg-white rounded-2xl p-8 hover:shadow-xl transition-all duration-300 group"
                            data-testid={`why-card-${index}`}
                        >
                            <div className="w-16 h-16 rounded-full bg-[#BF6CE4]/10 flex items-center justify-center text-[#BF6CE4] mb-6 group-hover:bg-[#BF6CE4] group-hover:text-white transition-all duration-300">
                                {reason.icon}
                            </div>
                            <h3 className="text-xl font-serif font-medium text-gray-900 mb-4">
                                {reason.title}
                            </h3>
                            <p className="text-gray-600 leading-relaxed">
                                {reason.description}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// Image Carousel
const ImageCarousel = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const allImages = [...GALLERY_IMAGES, { url: IMAGES.plating, alt: "Platillo gourmet" }, { url: IMAGES.gala, alt: "Evento de gala" }];
    
    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % allImages.length);
    };
    
    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
    };

    useEffect(() => {
        const timer = setInterval(nextSlide, 5000);
        return () => clearInterval(timer);
    }, []);

    return (
        <section className="py-16 bg-gray-50" data-testid="carousel-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="relative">
                    {/* Main Image */}
                    <div className="relative overflow-hidden rounded-2xl aspect-[16/9] lg:aspect-[21/9]">
                        <img 
                            src={allImages[currentIndex].url}
                            alt={allImages[currentIndex].alt}
                            className="w-full h-full object-cover transition-transform duration-700"
                        />
                    </div>
                    
                    {/* Navigation Arrows */}
                    <button 
                        onClick={prevSlide}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-all"
                        data-testid="carousel-prev"
                    >
                        <ChevronLeft size={24} className="text-gray-700" />
                    </button>
                    <button 
                        onClick={nextSlide}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg transition-all"
                        data-testid="carousel-next"
                    >
                        <ChevronRight size={24} className="text-gray-700" />
                    </button>
                    
                    {/* Dots */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {allImages.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentIndex(index)}
                                className={`w-2 h-2 rounded-full transition-all ${
                                    index === currentIndex ? 'bg-white w-6' : 'bg-white/50'
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// Services Section
const ServicesSection = () => {
    const services = [
        {
            icon: <Wine strokeWidth={1.5} size={32} />,
            title: "Planeación y Catering para Eventos",
            description: "Organización integral de eventos sociales, culturales y empresariales, incluyendo selección de menús personalizados, logística y ambientación.",
            image: "https://customer-assets.emergentagent.com/job_cucharas-landing/artifacts/ohkags2x_planeacion-catering.jpg"
        },
        {
            icon: <Utensils strokeWidth={1.5} size={32} />,
            title: "Preparación de Alimentos y Bebidas",
            description: "Catering de alta calidad con amplia variedad de platillos que destacan por su presentación, sabor y frescura.",
            image: "https://customer-assets.emergentagent.com/job_cucharas-landing/artifacts/x16uiwyt_alimentos-bebidas.jpg"
        },
        {
            icon: <CookingPot strokeWidth={1.5} size={32} />,
            title: "Comedores Industriales",
            description: "Menús nutritivos y balanceados adaptados a las necesidades específicas de tu empresa, garantizando máxima calidad.",
            image: IMAGES.industrial
        },
        {
            icon: <Cake strokeWidth={1.5} size={32} />,
            title: "Desarrollo de Menús",
            description: "Innovación constante para sorprender y deleitar, adaptándonos a tendencias y preferencias del mercado.",
            image: "https://customer-assets.emergentagent.com/job_cucharas-landing/artifacts/rw5e608o_desarrollo-menu.jpg"
        }
    ];

    return (
        <section id="servicios" className="py-24 lg:py-32 bg-white" data-testid="services-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
                    <div>
                        <span className="text-[#BF6CE4] font-semibold text-sm tracking-widest uppercase">
                            Nuestros
                        </span>
                        <h2 className="mt-2 text-4xl sm:text-5xl font-serif font-medium text-gray-900">
                            Servicios
                        </h2>
                    </div>
                    <p className="text-gray-600 max-w-md text-lg">
                        Soluciones gastronómicas integrales adaptadas a cada necesidad y ocasión.
                    </p>
                </div>

                {/* Services Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {services.map((service, index) => (
                        <div 
                            key={index}
                            className="group relative overflow-hidden rounded-2xl bg-gray-50 hover:bg-white hover:shadow-xl transition-all duration-500"
                            data-testid={`service-card-${index}`}
                        >
                            <div className="flex flex-col lg:flex-row">
                                {/* Image */}
                                <div className="lg:w-2/5 h-48 lg:h-auto overflow-hidden">
                                    <img 
                                        src={service.image}
                                        alt={service.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                {/* Content */}
                                <div className="lg:w-3/5 p-8">
                                    <div className="text-[#BF6CE4] mb-4">
                                        {service.icon}
                                    </div>
                                    <h3 className="text-xl font-serif font-medium text-gray-900 mb-3">
                                        {service.title}
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed mb-4">
                                        {service.description}
                                    </p>
                                    <a 
                                        href="#contacto" 
                                        className="inline-flex items-center gap-2 text-[#BF6CE4] font-semibold hover:gap-3 transition-all"
                                    >
                                        Saber más
                                        <ArrowRight size={16} />
                                    </a>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// About Section (Mission & Vision)
const AboutSection = () => {
    const features = [
        { icon: <ChefHat strokeWidth={1.5} />, title: "Pasión Culinaria", desc: "Corazón en cada receta" },
        { icon: <Salad strokeWidth={1.5} />, title: "Higiene Rigurosa", desc: "Estándares impecables" },
        { icon: <Coffee strokeWidth={1.5} />, title: "Hospitalidad", desc: "Servicio excepcional" },
        { icon: <Soup strokeWidth={1.5} />, title: "Versatilidad", desc: "Adaptación total" }
    ];

    return (
        <section id="nosotros" className="py-24 lg:py-32 bg-[#1C1422]" data-testid="about-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
                    {/* Left Column */}
                    <div>
                        <span className="text-[#BF6CE4] font-semibold text-sm tracking-widest uppercase">
                            Nosotros
                        </span>
                        <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-white leading-tight">
                            Más de 10 años sirviendo a las mejores empresas
                        </h2>
                        <div className="mt-12 space-y-8">
                            <div>
                                <div className="flex items-center gap-3 mb-3">
                                    <Utensils strokeWidth={1.5} size={24} className="text-[#BF6CE4]" />
                                    <h3 className="text-xl font-semibold text-white">Misión</h3>
                                </div>
                                <p className="text-gray-400 leading-relaxed">
                                    Nutrir y deleitar a nuestros clientes mediante servicios alimentarios de excelencia, 
                                    adaptándonos a cada entorno con calidez, eficiencia y calidad culinaria inigualable.
                                </p>
                            </div>
                            <div>
                                <div className="flex items-center gap-3 mb-3">
                                    <Beef strokeWidth={1.5} size={24} className="text-[#FF691F]" />
                                    <h3 className="text-xl font-semibold text-white">Visión</h3>
                                </div>
                                <p className="text-gray-400 leading-relaxed">
                                    Consolidarnos como el grupo gastronómico líder en la región, siendo referentes 
                                    en versatilidad de servicios y calidad humana.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Features */}
                    <div className="grid grid-cols-2 gap-6">
                        {features.map((feature, index) => (
                            <div 
                                key={index}
                                className="bg-white/5 backdrop-blur rounded-2xl p-6 hover:bg-white/10 transition-colors"
                                data-testid={`feature-${index}`}
                            >
                                <div className="text-[#BF6CE4] mb-4">
                                    {feature.icon}
                                </div>
                                <h4 className="text-white font-semibold mb-2">{feature.title}</h4>
                                <p className="text-gray-400 text-sm">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// Gallery Section
const GallerySection = () => {
    const [selectedImage, setSelectedImage] = useState(null);

    return (
        <section id="galeria" className="py-24 lg:py-32 bg-gray-50" data-testid="gallery-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="text-center mb-16">
                    <span className="text-[#BF6CE4] font-semibold text-sm tracking-widest uppercase">
                        Galería
                    </span>
                    <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-gray-900">
                        Nuestra cocina en imágenes
                    </h2>
                </div>

                {/* Gallery Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {GALLERY_IMAGES.map((image, index) => (
                        <div 
                            key={index}
                            className="group relative overflow-hidden rounded-xl cursor-pointer aspect-square"
                            onClick={() => setSelectedImage(image)}
                            data-testid={`gallery-image-${index}`}
                        >
                            <img 
                                src={image.url}
                                alt={image.alt}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                                <p className="text-white p-4 font-medium text-sm">{image.caption}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Lightbox Modal */}
            {selectedImage && (
                <div 
                    className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
                    onClick={() => setSelectedImage(null)}
                    data-testid="gallery-lightbox"
                >
                    <button 
                        className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
                        onClick={() => setSelectedImage(null)}
                        data-testid="lightbox-close"
                    >
                        <X size={32} />
                    </button>
                    <div className="max-w-5xl max-h-[85vh] relative" onClick={(e) => e.stopPropagation()}>
                        <img 
                            src={selectedImage.url}
                            alt={selectedImage.alt}
                            className="max-w-full max-h-[85vh] object-contain rounded-lg"
                        />
                        <p className="text-white text-center mt-4 text-lg">{selectedImage.caption}</p>
                    </div>
                </div>
            )}
        </section>
    );
};

// Testimonials Section
const TestimonialsSection = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const testimonials = [
        {
            quote: "El servicio de comedor para nuestros 500 empleados ha sido impecable; la variedad y el sabor han mejorado notablemente el clima laboral.",
            author: "Manufacturas del Norte",
            role: "Cliente Corporativo"
        },
        {
            quote: "Cucharas y Sabores es mi aliado número uno; su capacidad para rentar desde la vajilla hasta los instrumentos musicales facilita todo el proceso.",
            author: "Lic. Martha Solís",
            role: "Organizadora de Bodas"
        },
        {
            quote: "Como socios en la modalidad de comida para llevar, su estandarización de procesos nos ha permitido crecer sin sacrificar el sabor que nuestros clientes aman.",
            author: "Restaurante El Portal",
            role: "Socio Comercial"
        }
    ];

    return (
        <section id="testimonios" className="py-24 lg:py-32 bg-white" data-testid="testimonials-section">
            <div className="max-w-5xl mx-auto px-6 lg:px-8">
                <div className="text-center mb-16">
                    <span className="text-[#BF6CE4] font-semibold text-sm tracking-widest uppercase">
                        Testimonios
                    </span>
                    <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-gray-900">
                        Lo que dicen nuestros clientes
                    </h2>
                </div>

                {/* Testimonial Carousel */}
                <div className="relative">
                    <div className="text-center">
                        <Quote size={48} className="mx-auto text-[#FFD966] mb-8" />
                        <blockquote className="text-2xl sm:text-3xl font-serif text-gray-800 leading-relaxed italic">
                            "{testimonials[activeIndex].quote}"
                        </blockquote>
                        <div className="mt-8">
                            <p className="font-semibold text-gray-900 text-lg">{testimonials[activeIndex].author}</p>
                            <p className="text-[#BF6CE4]">{testimonials[activeIndex].role}</p>
                        </div>
                    </div>

                    {/* Navigation Dots */}
                    <div className="flex justify-center gap-3 mt-12">
                        {testimonials.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setActiveIndex(index)}
                                className={`w-3 h-3 rounded-full transition-all ${
                                    index === activeIndex ? 'bg-[#BF6CE4] w-8' : 'bg-gray-300 hover:bg-gray-400'
                                }`}
                                data-testid={`testimonial-dot-${index}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

// CTA Banner
const CTABanner = () => {
    return (
        <section className="py-8 bg-[#FF691F] overflow-hidden" data-testid="cta-banner">
            <div className="flex animate-marquee whitespace-nowrap">
                {[...Array(10)].map((_, i) => (
                    <span key={i} className="mx-8 text-white/90 text-lg font-medium flex items-center gap-4">
                        Estamos para escucharte
                        <span className="text-[#FFD966]">✦</span>
                    </span>
                ))}
            </div>
        </section>
    );
};

// Contact Section
const ContactSection = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            await emailjs.send(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                {
                    from_name: formData.name,
                    from_email: formData.email,
                    phone: formData.phone || 'No proporcionado',
                    message: formData.message,
                    to_name: 'Cucharas y Sabores'
                },
                EMAILJS_PUBLIC_KEY
            );
            
            await axios.post(`${API}/contact`, formData);
            
            setSubmitStatus('success');
            setFormData({ name: '', email: '', phone: '', message: '' });
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section id="contacto" className="py-24 lg:py-32 bg-gray-50" data-testid="contact-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                    {/* Contact Info */}
                    <div>
                        <span className="text-[#BF6CE4] font-semibold text-sm tracking-widest uppercase">
                            Contacto
                        </span>
                        <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-gray-900">
                            Ponte en contacto con nuestro equipo
                        </h2>
                        <p className="mt-6 text-gray-600 text-lg leading-relaxed">
                            ¿Te interesa trabajar con nosotros? Mándanos un mensaje y será un gusto ayudarte.
                        </p>

                        <div className="mt-12 space-y-6">
                            <a href="tel:+523329513196" className="flex items-center gap-4 group">
                                <div className="w-14 h-14 rounded-full bg-[#BF6CE4]/10 flex items-center justify-center text-[#BF6CE4] group-hover:bg-[#BF6CE4] group-hover:text-white transition-all">
                                    <Phone strokeWidth={1.5} size={24} />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Teléfono</p>
                                    <p className="text-gray-900 font-medium">332 951 3196</p>
                                </div>
                            </a>

                            <a href="mailto:contacto@cucharasysabores.com" className="flex items-center gap-4 group">
                                <div className="w-14 h-14 rounded-full bg-[#BF6CE4]/10 flex items-center justify-center text-[#BF6CE4] group-hover:bg-[#BF6CE4] group-hover:text-white transition-all">
                                    <Mail strokeWidth={1.5} size={24} />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Email</p>
                                    <p className="text-gray-900 font-medium">contacto@cucharasysabores.com</p>
                                </div>
                            </a>

                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-[#BF6CE4]/10 flex items-center justify-center text-[#BF6CE4]">
                                    <MapPin strokeWidth={1.5} size={24} />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Dirección</p>
                                    <p className="text-gray-900 font-medium">
                                        Calle Enrique Colunga 488, Col. Pedro Moreno<br />
                                        Zapopan, Jalisco, México
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
                        <form onSubmit={handleSubmit} data-testid="contact-form">
                            <div className="space-y-6">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                        Nombre completo
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-4 rounded-xl border border-gray-200 focus:border-[#BF6CE4] focus:ring-2 focus:ring-[#BF6CE4]/20 transition-all bg-gray-50"
                                        placeholder="Tu nombre"
                                        data-testid="input-name"
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-4 rounded-xl border border-gray-200 focus:border-[#BF6CE4] focus:ring-2 focus:ring-[#BF6CE4]/20 transition-all bg-gray-50"
                                            placeholder="tu@email.com"
                                            data-testid="input-email"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                                            Teléfono
                                        </label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full px-4 py-4 rounded-xl border border-gray-200 focus:border-[#BF6CE4] focus:ring-2 focus:ring-[#BF6CE4]/20 transition-all bg-gray-50"
                                            placeholder="Tu teléfono"
                                            data-testid="input-phone"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                                        Mensaje
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows={4}
                                        className="w-full px-4 py-4 rounded-xl border border-gray-200 focus:border-[#BF6CE4] focus:ring-2 focus:ring-[#BF6CE4]/20 transition-all bg-gray-50 resize-none"
                                        placeholder="¿En qué podemos ayudarte?"
                                        data-testid="input-message"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full py-4 bg-[#BF6CE4] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#A552C9] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid="submit-button"
                                >
                                    {isSubmitting ? (
                                        'Enviando...'
                                    ) : (
                                        <>
                                            Enviar Mensaje
                                            <Send size={18} />
                                        </>
                                    )}
                                </button>

                                {submitStatus === 'success' && (
                                    <div className="p-4 bg-green-50 border border-green-200 text-green-700 text-center rounded-xl" data-testid="success-message">
                                        ¡Gracias por tu mensaje! Nos pondremos en contacto contigo pronto.
                                    </div>
                                )}

                                {submitStatus === 'error' && (
                                    <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-center rounded-xl" data-testid="error-message">
                                        Hubo un error al enviar tu mensaje. Por favor, intenta de nuevo.
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

// Footer
const Footer = () => {
    return (
        <footer className="bg-[#1C1422] py-16" data-testid="footer">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
                    {/* Logo & Description */}
                    <div className="md:col-span-2">
                        <img 
                            src={LOGO_URL} 
                            alt="Cucharas y Sabores" 
                            className="h-16 w-auto brightness-0 invert mb-6"
                        />
                        <p className="text-gray-400 leading-relaxed max-w-md">
                            Grupo gastronómico apasionado por la alta cocina y experto en soluciones gastronómicas integrales para empresas y eventos.
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="text-white font-semibold mb-6">Enlaces</h4>
                        <nav className="space-y-3">
                            {['Inicio', 'Servicios', 'Nosotros', 'Testimonios', 'Contacto'].map((item) => (
                                <a
                                    key={item}
                                    href={`#${item.toLowerCase()}`}
                                    className="block text-gray-400 hover:text-[#BF6CE4] transition-colors"
                                >
                                    {item}
                                </a>
                            ))}
                        </nav>
                    </div>

                    {/* Contact Info */}
                    <div>
                        <h4 className="text-white font-semibold mb-6">Contacto</h4>
                        <div className="space-y-3 text-gray-400">
                            <p>332 951 3196</p>
                            <p>contacto@cucharasysabores.com</p>
                            <p>Calle Enrique Colunga 488, col Pedro Moreno, Zapopan, Jalisco</p>
                        </div>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-gray-800 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <p className="text-gray-500 text-sm">
                        © {new Date().getFullYear()} Cucharas y Sabores. Todos los derechos reservados.
                    </p>
                </div>
            </div>
        </footer>
    );
};

// Main App Component
function App() {
    return (
        <div className="App">
            <Header />
            <main>
                <HeroSection />
                <IntroSection />
                <WhyChooseUsSection />
                <ServicesSection />
                <AboutSection />
                <TestimonialsSection />
                <CTABanner />
                <ContactSection />
            </main>
            <Footer />
        </div>
    );
}

export default App;
